/**
 * THE HOUSEHOLD'S SAVED METHOD AND ITS STOP, one card and one Stop state for the two pages that draw
 * them: Payments (`screens/Payments.tsx`) and Account's How you pay (`screens/AccountPaying.tsx`,
 * slice A2). Both read GET /api/payments, and both draw the method and her Stop by the same code
 * from the same answer, so the two never say different things of it (`lib/standing.ts`).
 *
 * - THE CARD: "Charges automatically: Visa ending 4242, since 12 Oct", in the seam's words, with Stop
 *   under it, whenever ops says a method stands (the seam, l.197-199 at b727ec1); and where her Stop
 *   stands, as ops has it, in a live region, so what ops answers while she waits is read out.
 *   Stopped and nothing saved are said in place of the method, which ops says no longer charges. A
 *   Stop ops refused as naming a method that no longer stands (ops's section 11.5, slice M2 of round
 *   four) is said under the method the copy holds, with the phone and no Stop, until ops's push says
 *   which stands (`changed`). What a page says after that is the page's own (`children`).
 * - STOP opens the Stop sheet (`StopSheet.tsx`), which asks for a fresh code when the door does.
 *   Ops decides (CLAUDE.md binding rule 1): a Stop sent says "Sent to the office." and offers no Stop
 *   while it is on its way, and the method is said to be stopped, in place of it, only once ops's
 *   receipt says so (`stopAsk`, as the Worker reads it), or its push says nothing is saved after the
 *   Stop sent from this page. While the Stop is on its way, the page is read again every three
 *   seconds, at most six times, so what ops answers is said without a reload.
 * - THAT SHE PAYS EACH INVOICE HERSELF is each page's to say (`paysYourself`), only where no card is
 *   drawn and no owed invoice is being charged now by a saved method (`chargeClaimed`): a Stop does
 *   not cancel a charge at Stripe, so ops can say nothing is saved while that invoice's card on
 *   Payments says it is being charged now, and the page then says nothing of how she pays.
 * - THE FOCUS: back on Stop when the sheet closes having asked nothing, and on the card when the
 *   door's answer has closed it, so what the card now says is where she is; on the card where Stop
 *   has gone, and on the page's heading where a read since holds no card either.
 * - A SHEET WHOSE METHOD A READ SINCE NO LONGER HOLDS, as after the door's 404, stays drawn on the
 *   method as it stood when it opened, so what it says of that 404 is never taken away in silence
 *   (the owner, 2026-10-20).
 */
import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import type { PaymentsPlace, SavedMethod } from '../api.ts';
import type { Asking } from '../lib/asks.ts';
import { shortDate } from '../lib/dates.ts';
import { methodKey, standing, type Standing } from '../lib/standing.ts';
import { chargesAutomatically, FAILED_BEFORE_PHONE, REQUEST, STOP } from '../words.ts';
import { CARD_BUTTON } from './buttons.ts';
import type { Signed } from './Guarded.tsx';
import { Phone } from './Phone.tsx';
import { focusPageHeading } from './RouteFocus.tsx';
import { chargeClaimed } from './SavedMethodPays.tsx';
import { StopSheet } from './StopSheet.tsx';

/** How long a page waits between reads while her Stop is on its way to ops. */
export const STOP_POLL_GAP_MS = 3000;

/** How many times at most a page is read again while her Stop is on its way. */
export const STOP_POLLS = 6;

/** The card's own parts, as `useStop` hands them to `SavedMethodCard`. */
export interface SavedMethodCardProps {
  /** The method that stands, or null once ops says nothing is saved. */
  readonly method: SavedMethod | null;
  readonly said: Standing;
  readonly cardRef: Ref<HTMLDivElement>;
  readonly stopRef: Ref<HTMLButtonElement>;
  readonly onStop: () => void;
}

/** Where the household's saved method stands on a page, and what that page draws of it. */
export interface Stopping {
  /** What the card says, or null with no method and no Stop of hers to say anything of. */
  readonly said: Standing | null;
  /**
   * The method stands, a Stop of it sent or not, or one ops refused as naming another, until ops
   * says it stopped or nothing is saved.
   */
  readonly methodStands: boolean;
  /** The Stop sheet while it is open, drawn over the page (`Shell`'s `over`). */
  readonly sheet: ReactNode;
  /** The card's parts, while `said` is not null. */
  readonly card: SavedMethodCardProps | null;
  /**
   * The page may say she pays each invoice herself: no card is drawn, and no owed invoice is being
   * charged now by a saved method (`chargeClaimed`), which a Stop does not cancel, so that its card
   * on Payments and the page never say two things of one answer.
   */
  readonly paysYourself: boolean;
}

/**
 * The one Stop state a page keeps: whether the sheet is open, the method a Stop sent from the page
 * was about, whether the door said nothing was saved, where the focus goes, the Stop out and its
 * key, and the reads again while it is on its way.
 */
export function useStop(place: PaymentsPlace, signed: Signed, reload: () => void): Stopping {
  const { autopay } = place;
  /**
   * The Stop sheet is open, on the method as it stood when it opened, which keeps the sheet drawn
   * where a read since holds no method, so what it says of the door's 404 is never taken away in
   * silence (the owner, 2026-10-20); null while it is closed.
   */
  const [stopping, setStopping] = useState<SavedMethod | null>(null);
  /** The method the door wrote a Stop about from this page (`methodKey`), or null for none. */
  const [sentFor, setSentFor] = useState<string | null>(null);
  /** The door said nothing was saved to stop. */
  const [nothingSaved, setNothingSaved] = useState(false);
  /** Where the focus goes once the sheet has gone: back to Stop, or to what the card says. */
  const [focus, setFocus] = useState<{ readonly to: 'stop' | 'card' } | null>(null);
  /** Whether a Stop is out, and its key, which outlive the sheet (`lib/asks.ts`). */
  const asking = useRef<Asking>({ out: false, keys: new Map() });
  const stopButton = useRef<HTMLButtonElement>(null);
  const card = useRef<HTMLDivElement>(null);
  /** How many times the page has been read again while the Stop is on its way. */
  const polls = useRef(0);

  // Back on Stop, or on the card where Stop has gone, or, where a read since holds no card either,
  // on the page's heading (`focusPageHeading`), so the focus is never left on nothing.
  useEffect(() => {
    if (focus === null) return;
    const back = (focus.to === 'stop' ? stopButton.current : null) ?? card.current;
    if (back === null) focusPageHeading();
    else back.focus();
  }, [focus]);

  // Her Stop is on its way to ops: read the page again, every three seconds, at most six times.
  const stopOnItsWay = autopay !== null && place.stopAsk?.state === 'sending';
  useEffect(() => {
    if (!stopOnItsWay || polls.current >= STOP_POLLS) return;
    const timer = setTimeout(() => {
      polls.current += 1;
      reload();
    }, STOP_POLL_GAP_MS);
    return () => clearTimeout(timer);
  }, [place, stopOnItsWay, reload]);

  const said = standing(place, sentFor, nothingSaved);
  const closeStop = () => {
    setStopping(null);
    setFocus({ to: 'stop' });
  };
  // The door may answer after the sheet has closed: the page still says it, with the focus.
  const toCard = () => {
    setStopping(null);
    setFocus({ to: 'card' });
    reload();
  };

  /** The method the open sheet is about: as the page now holds it, else as it stood at opening. */
  const method = stopping === null ? null : (autopay ?? stopping);
  const sheet =
    method !== null ? (
      <StopSheet
        label={method.label}
        methodType={method.methodType}
        signedInWith={signed.signedInWith}
        household={place.household.id}
        asking={asking.current}
        onClose={closeStop}
        onSent={() => {
          setSentFor(methodKey(method));
          polls.current = 0;
          toCard();
        }}
        onNothingSaved={() => {
          setNothingSaved(true);
          toCard();
        }}
        onSignedOut={signed.onSignedOut}
        onStale={reload}
      />
    ) : undefined;

  return {
    said,
    // The household's method stands, a Stop of it sent or not, or one ops refused as naming another,
    // until ops says it stopped or nothing is saved: only then does a card say what it will do.
    methodStands:
      said !== null && (said.kind === 'offered' || said.kind === 'sent' || said.kind === 'changed'),
    sheet,
    card:
      said === null
        ? null
        : {
            method: autopay,
            said,
            cardRef: card,
            stopRef: stopButton,
            onStop: () => {
              setFocus(null);
              setStopping(autopay);
            },
          },
    paysYourself: said === null && !place.toPay.some(chargeClaimed),
  };
}

/**
 * "Charges automatically: Visa ending 4242, since 12 Oct", in a card of its own, with Stop under it;
 * and where her Stop stands, as ops has it, in a live region, so what ops answers while she waits is
 * read out. Stopped and nothing saved are said in place of the method, which ops says no longer
 * charges, and `children` follow them: what the page says next. A Stop ops refused as naming a
 * method that no longer stands (ops's section 11.5) is said under the method the copy holds, with
 * the phone and no Stop, until ops's push says which stands.
 */
export function SavedMethodCard({
  method,
  said,
  cardRef,
  stopRef,
  onStop,
  children,
}: SavedMethodCardProps & { readonly children?: ReactNode }) {
  const since = method?.agreedOn === undefined ? null : shortDate(method.agreedOn);
  const gone = said.kind === 'stopped' || said.kind === 'nothing_saved';
  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      data-saved-method
      className="rounded-card border border-border-subtle bg-surface-raised px-5 py-4"
    >
      {gone || method === null ? null : (
        <p className="text-body text-pretty text-text-body">
          {chargesAutomatically(method.label, method.methodType, since)}
        </p>
      )}
      <div aria-live="polite">
        {said.kind === 'stopped' ? (
          <p className="text-body text-pretty text-text-body">{STOP.stopped}</p>
        ) : said.kind === 'nothing_saved' ? (
          <p className="text-body text-pretty text-text-body">{STOP.nothingSaved}</p>
        ) : said.kind === 'sent' ? (
          <p className="mt-2 text-small text-pretty text-text-muted">{REQUEST.sent}</p>
        ) : said.kind === 'changed' ? (
          <p className="mt-2 text-small text-pretty text-text-body">
            {STOP.methodChangedBeforePhone} <Phone after="." />
          </p>
        ) : said.kind !== 'offered' || said.said === undefined ? null : (
          <p className="mt-2 text-small text-pretty text-text-body">
            {said.said === 'closed' ? STOP.cannotBeforePhone : FAILED_BEFORE_PHONE}{' '}
            <Phone after="." />
          </p>
        )}
      </div>
      {said.kind === 'offered' ? (
        <button
          ref={stopRef}
          type="button"
          aria-haspopup="dialog"
          onClick={onStop}
          className={`mt-4 cursor-pointer ${CARD_BUTTON}`}
        >
          {STOP.stop}
        </button>
      ) : null}
      {children}
    </div>
  );
}
